import type { FC } from "react";

import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";

import { Dialog } from "../Dialog";

import styles from "./ConfirmationDialog.module.scss";

export type ConfirmationDialogProps = {
  isOpened: boolean;
  title?: string;
  subTitle?: string | React.ReactNode;
  header?: string | React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  dialogClassName?: string;
  mainContentClassName?: string;
  titleClassName?: string;
  onConfirm?(): void;
  onCancel?(): void;
  isPending?: boolean;
  portalDomNode?: HTMLElement;
  footer?: string | React.ReactNode;
};

export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  isOpened,
  title,
  subTitle,
  isPending = false,
  confirmButtonText,
  cancelButtonText,
  dialogClassName = "",
  mainContentClassName = "",
  titleClassName = "",
  header,
  onConfirm,
  onCancel,
  portalDomNode,
  footer,
}) => {
  const { __ } = useTranslations();
  const { breakpoint } = useDeviceBreakpoint();
  return (
    <Dialog
      isOpened={isOpened}
      fullscreen={breakpoint === "mobile"}
      onClose={onCancel}
      className={classNames(styles.dialogWrapper, dialogClassName)}
      portalDomNode={portalDomNode}
      contentWrapperClassName={styles.dialogContentWrapper}
    >
      <div className={classNames(styles.dialog, mainContentClassName)}>
        {title && (
          <div className={`${styles.title} ${titleClassName}`} data-testid="confirmationDialogTitle">
            {title}
          </div>
        )}
        {header && (
          <div className={styles.header} data-testid="confirmationDialogHeader">
            {header}
          </div>
        )}
        {subTitle && (
          <div className={styles.subTitle} data-testid="confirmationDialogSubTitle">
            {subTitle}
          </div>
        )}
        <div className={classNames(styles.btnGroup)}>
          <Button data-testid="confirmationDialogConfirmButton" pending={isPending} onClick={onConfirm} size="lg">
            {confirmButtonText || __("libs.ui.confirmation_dialog.confirm_button")}
          </Button>
          <Button data-testid="confirmationDialogCancelButton" variant="secondary" onClick={onCancel} size="lg">
            {cancelButtonText || __("libs.ui.confirmation_dialog.cancel_button")}
          </Button>
        </div>
        <div className={classNames(styles.btnGroup, styles.mobileBtnGroup)}>
          <Button
            data-testid="confirmationDialogConfirmButton"
            pending={isPending}
            onClick={onConfirm}
            size="sm"
            fullWidth
          >
            {confirmButtonText || __("libs.ui.confirmation_dialog.confirm_button")}
          </Button>
          <Button
            data-testid="confirmationDialogCancelButton"
            variant="secondary"
            onClick={onCancel}
            size="sm"
            fullWidth
          >
            {cancelButtonText || __("libs.ui.confirmation_dialog.cancel_button")}
          </Button>
        </div>
        {footer && (
          <div className={styles.footer} data-testid="confirmationDialogFooter">
            {footer}
          </div>
        )}
      </div>
    </Dialog>
  );
};
